import React, {
	useMemo,
	useState,
} from 'react';
import { Flex } from 'antd';
import { TableOutlined } from '@ant-design/icons';
import { Values } from '@shared/utils/objectEnums';
import {
	Currencies,
	FixtureTypes,
} from '@shared/utils/constants';
import { capitalize } from '@shared/utils/string';
import type {
	GetFixtureDetailsResponse,
	PeriodSchedule,
} from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import TcTerms from '@api/models/tc-terms';
import Button from '@client/components/Button';
import EditableCellTableRedux from '@client/components/EditableTableRedux/EditableCellTableRedux';
import PeriodScheduleModal from './PeriodScheduleModal';
import { periodScheduleDateColumn } from './periodScheduleHelpers';

const PeriodScheduleTable = ({
	fixture,
	periodSchedule,
	fixtureCurrency,
	onRowChange,
	refreshFixture,
	afterSave,
	hireEditable = false,
	modalEditable = true,
	tcTerms,
}: {
	fixture: GetFixtureDetailsResponse<TcFixtureProps> | GetFixtureDetailsResponse<BbFixtureProps>;
	periodSchedule: PeriodSchedule[];
	onRowChange?: (row: PeriodSchedule) => void;
	fixtureCurrency: Values<typeof Currencies>;
	refreshFixture: () => void;
	afterSave?: () => void;
	hireEditable?: boolean;
	modalEditable?: boolean;
	tcTerms: TcTerms,
}) => {
	const [periodScheduleDrawerOpen, setPeriodScheduleDrawerOpen] = useState<boolean | number>(false);
	const periodScheduleKey = useMemo(
		() => periodSchedule.map((e) => `${e.id}.${e.order}`).join('-'),
		[periodSchedule],
	);

	const columns = useMemo(() => [
		{
			title: 'Period',
			editable: false,
			type: 'text',
			dataIndex: 'order',
		},
		{
			title: 'Type',
			dataIndex: 'periodType',
			type: 'text',
			editable: false,
			render: ({ periodType }: PeriodSchedule) => capitalize(periodType),
		},
		periodScheduleDateColumn('from', 'Start'),
		periodScheduleDateColumn('to', 'End (min)'),
		periodScheduleDateColumn('maxTo', 'End (max)'),
		...(fixture.type !== FixtureTypes.BB_OUT ? [{
			title: 'Hire rate',
			dataIndex: 'hireRate',
			editable: hireEditable,
			type: 'currency',
			key: periodScheduleKey,
			currency: fixtureCurrency,
			// @ts-ignore
			inputProps: {
				currency: fixtureCurrency,
				addonAfter: tcTerms.hireUnit ? '/day' : '/month',
				required: true,
				asAsync: true,
				highlightOnNull: true,
			},
		}] : []),
	], [fixture.type, hireEditable, fixtureCurrency, periodScheduleKey, tcTerms.hireUnit]);

	const hasSchedule = periodSchedule?.length > 0;

	const scheduleContent = useMemo(() => {
		return (
			<>
				<Flex vertical>
					<EditableCellTableRedux
						rowKey="id"
						pagination={false}
						size="small"
						// @ts-ignore
						columns={columns}
						dataSource={periodSchedule}
						onRowChange={onRowChange}
						locale={{ emptyText: 'Enter a laycan date to see the schedule' }}
					/>
					{modalEditable && (
						<>
							<Flex justify="center">
								<Button
									onClick={() => setPeriodScheduleDrawerOpen(true)}
									type="link"
									icon={(<TableOutlined />)}
									disabled={!hasSchedule}
									disabledTooltip="Enter a laycan date to see the schedule"
								>
									Edit Periods & Rates
								</Button>
							</Flex>
							<PeriodScheduleModal
								periodSchedule={periodSchedule}
								setPeriodScheduleDrawerOpen={setPeriodScheduleDrawerOpen}
								periodScheduleDrawerOpen={periodScheduleDrawerOpen}
								fixture={fixture as GetFixtureDetailsResponse<TcFixtureProps>}
								refreshFixture={refreshFixture}
								fixtureCurrency={fixtureCurrency}
								afterSave={afterSave}
								disabled={!modalEditable}
								tcTerms={tcTerms}
							/>
						</>
					)}
				</Flex>
			</>
		);
	}, [
		columns,
		setPeriodScheduleDrawerOpen,
		fixture,
		fixtureCurrency,
		periodScheduleDrawerOpen,
		refreshFixture,
		afterSave,
		onRowChange,
		periodSchedule,
		hasSchedule,
		modalEditable,
		tcTerms,
	]);

	return (<Flex vertical gap={10}>{scheduleContent}</Flex>);
};

export default PeriodScheduleTable;
